/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';

/**
 * @prop {Number} pages - Defines the total number of pages.
 * @prop {Number} currentPage - Default 1. Defines which page is currently active.
 * @attr {Number} current-page - Default 1. Defines which page is currently active.
 * @fires clicked-page-move	- Dispatched when clicking on a page number.
 * @fires previous-page-move - Dispatched when clicking on the "previous" arrow.
 * @fires next-page-move - Dispatched when clicking on the "next" arrow.
 */
export class SHPagination extends LitElement {
  static get properties() {
    return {
      pages: { type: Number, reflect: true },
      currentPage: { type: Number, reflect: true, attribute: 'current-page' },
    };
  }

  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          display: block;
          position: relative;
          width: 100%;
          white-space: nowrap;
        }

        .pagination-wrapper {
          list-style: none;
          width: fit-content;
          width: -ms-fit-content;
          width: -moz-fit-content;
          display: flex;
          height: 1.5rem;
          margin: auto;
          transition: all var(--transition-time) ease-in-out;
        }

        .pagination-wrapper ul {
          display: inline-flex;
          list-style: none;
          margin: 0 auto;
          padding: 0;
          height: 100%;
        }

        .pagination-wrapper ul li {
          height: 1.5rem;
          width: 1.5rem;
          text-align: center;
          margin-right: 0.5rem;
          font: var(--title-1);
          line-height: 1.5rem;
          border-radius: 50%;
          color: var(--text-secondary);
          background: transparent;
          cursor: pointer;
          transition: all var(--transition-time) ease-in-out;
          font-weight: 700;
          user-select: none;
          -moz-user-select: none;
          -webkit-user-select: none;
        }

        .pagination-wrapper ul li:first-child,
        .pagination-wrapper ul li:last-child {
          height: 2rem;
          width: 2rem;
        }

        .condensed ul li {
          font-size: 0.625rem;
        }

        .pagination-wrapper ul li:first-of-type {
          margin-right: 1rem;
        }

        .pagination-wrapper ul li:last-of-type {
          margin-right: 0rem;
        }

        .pagination-wrapper ul li sh-icon {
          position: unset;
        }

        .prevPageIcon.disabled,
        .nextPageIcon.disabled {
          cursor: default;
          pointer-events: none;
        }

        .prevPageIcon.disabled sh-icon {
          pointer-events: none;
        }

        .nextPageIcon.disabled sh-icon {
          pointer-events: none;
        }

        .pagination-wrapper li[active] {
          background: rgba(var(--ui-1), var(--opacity-6));
          color: var(--text-primary);
        }

        /* State Styles */

        .pagination-wrapper ul .sh-pagination-item[click-disabled] {
          pointer-events: none;
        }

        /* Focus Styles - Accessibility */

        :focus,
        :active {
          outline: 0;
        }

        .nextPageIcon sh-icon {
          margin-top: -0.25rem;
        }

        .prevPageIcon sh-icon {
          margin-top: -0.25rem;
        }

        .nextPageIcon {
          margin-left: 0.5rem;
        }

        /* hover */

        :host(.touch-device) .pagination-wrapper ul .sh-pagination-item:hover,
        :host(.touch-device) .pagination-wrapper ul .sh-pagination-item.hover,
        :host(:not(.touch-device)) .pagination-wrapper ul .sh-pagination-item:hover,
        :host .pagination-wrapper .sh-pagination-item.hover {
          background: rgba(var(--ui-1), var(--opacity-7));
          transition: all var(--transition-time) ease-in-out;
          color: var(--text-primary);
        }
      `,
    ];
  }

  render() {
    return html` <div class="pagination-wrapper ${this.pages > 999 ? 'condensed' : ''}">
      <ul id="ul">
        <li
          title="Previous Page"
          @click="${this._prevPage}"
          class="prevPageIcon"
          id="leftArrowWrapper"
        >
          <sh-icon
            icon="arrow-line-left-s"
            size="m"
            button
            ?disabled=${this.currentPage <= 1}
          ></sh-icon>
        </li>
        ${this._getPaginationItems()}
        <li
          title="Next Page"
          @click="${this._nextPage}"
          class="nextPageIcon"
          id="rightArrowWrapper"
        >
          <sh-icon
            icon="arrow-line-right-s"
            size="m"
            button
            ?disabled=${this.currentPage >= this.pages}
          ></sh-icon>
        </li>
      </ul>
    </div>`;
  }

  _getPaginationItems() {
    let paginationValues = this._getPaginationValues();
    let paginationItems = [];
    for (let item = 0; item < paginationValues.length; item++) {
      paginationItems.push(html`<li
        class="sh-pagination-item"
        id="item"
        @click="${this._handleClick}"
        ?active=${paginationValues[item] === this.currentPage}
        ?click-disabled=${paginationValues[item].toString() === '...'}
      >
        ${paginationValues[item]}
      </li>`);
    }
    return paginationItems;
  }

  firstUpdated() {
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('touch-device');
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('currentPage') || changedProperties.has('pages')) {
      this.currentPage = !this.currentPage ? 1 : this.currentPage;
    }
    if (changedProperties.has('pages')) {
      this.style.display = this.pages <= 1 ? 'none' : 'block';
    }
    const listOfProperties = ['pages', 'currentPage'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  _getPaginationValues() {
    this.pages = !this.pages ? 1 : this.pages;
    let paginationValues = [];
    if (this.pages <= 7) {
      paginationValues = this._getPaginationValuesWhenPagesLessThanSeven();
    } else {
      paginationValues = this._getPaginationValuesWhenPagesGreaterThanSeven();
    }
    return paginationValues;
  }

  _getPaginationValuesWhenPagesLessThanSeven() {
    let paginationValues = [];
    for (let page = 1; page <= this.pages; page++) {
      paginationValues.push(page);
    }
    return paginationValues;
  }

  _getPaginationValuesWhenPagesGreaterThanSeven() {
    const fourthLastPage = this.pages - 3;
    let paginationValues = [];
    /*If current page = 1 or 2 or 3 or 4 , totalArray = [1 2 3 4 5 ... 20]*/
    if (this.currentPage <= 4) {
      paginationValues = this._getPaginationValuesWhenCurrentPageLessthanFour();
    } else if (this.currentPage > 4 && this.currentPage < fourthLastPage) {
      /*If current page = x , totalArray = [1 ... (x-1) (x) (x+1) ... N], where N = total-pages */
      paginationValues = this._getPaginationValuesWhenCurrentPageGreaterthanFour();
    } else {
      /*If current page = 17 or 18 or 19 or 20, totalArray = [1 ... 16 17 18 19 20] */
      paginationValues = this._getPaginationValuesWhenCurrentPageGreaterthanFourthlast();
    }
    return paginationValues;
  }

  _getPaginationValuesWhenCurrentPageLessthanFour() {
    let paginationValues = [];
    for (let page = 1; page <= 7; page++) {
      if (page <= 5) {
        paginationValues.push(page);
      } else if (page === 6) {
        paginationValues.push('...');
      } else {
        paginationValues.push(this.pages);
      }
    }
    return paginationValues;
  }

  _getPaginationValuesWhenCurrentPageGreaterthanFour() {
    let paginationValues = [];
    for (let page = 1; page <= 5; page++) {
      if (page === 1) {
        paginationValues.push(page);
      } else if (page === 2 || page === 4) {
        paginationValues.push('...');
      } else if (page === 3) {
        for (let j = 1; j <= 3; j++) {
          switch (j) {
            case 1:
              paginationValues.push(this.currentPage - 1);
              break;
            case 2:
              paginationValues.push(this.currentPage);
              break;
            case 3:
              paginationValues.push(this.currentPage + 1);
              break;
          }
        }
      } else {
        paginationValues.push(this.pages);
      }
    }
    return paginationValues;
  }

  _getPaginationValuesWhenCurrentPageGreaterthanFourthlast() {
    let paginationValues = [];
    const fourthLastPage = this.pages - 3;
    for (let page = 1; page <= 3; page++) {
      switch (page) {
        case 1:
          paginationValues.push(page);
          break;
        case 2:
          paginationValues.push('...');
          break;
        case 3:
          {
            let subValue = 1;
            for (let j = 1; j <= 5; j++) {
              paginationValues.push(fourthLastPage - subValue);
              --subValue;
            }
          }
          break;
      }
    }
    return paginationValues;
  }

  _handleClick(e) {
    let clickedItem, isNotANumber;
    clickedItem = Number(e.target.innerText);
    isNotANumber = isNaN(clickedItem);
    if (!isNotANumber) {
      this.currentPage = clickedItem;
      dispatchCustomEvent(this, 'clicked-page-move');
    }
  }
  _prevPage() {
    let prevCurrentPage = this.currentPage - 1;
    if (prevCurrentPage >= 1) {
      this.currentPage = prevCurrentPage;
      dispatchCustomEvent(this, 'previous-page-move');
    }
  }
  _nextPage() {
    let nextCurrentPage = this.currentPage + 1;
    if (nextCurrentPage <= this.pages) {
      this.currentPage = nextCurrentPage;
      dispatchCustomEvent(this, 'next-page-move');
    }
  }
}

customElements.define('sh-pagination', SHPagination);
